/** Content area height: 297mm page − 20mm top − 20mm bottom padding. */
export const A4_PAGE_CONTENT_HEIGHT_MM = 257;

const MM_TO_PX_RATIO = 3.7795275591;

const MEASURE_HOST_STYLE = [
  'position:absolute',
  'left:-9999px',
  'top:0',
  'visibility:hidden',
  'width:calc(210mm - 36mm)',
  'font-family:Arial, Helvetica, sans-serif',
  'font-size:11pt',
  'line-height:1.5',
  'white-space:pre-wrap',
  'overflow-wrap:anywhere',
  'box-sizing:border-box',
].join(';');

const isMeaningfulNode = node => {
  if (node.nodeType === Node.TEXT_NODE) {
    return Boolean(node.textContent && node.textContent.trim());
  }
  return node.nodeType === Node.ELEMENT_NODE;
};

const mmToPx = mm => {
  if (typeof document === 'undefined') {
    return mm * MM_TO_PX_RATIO;
  }

  const el = document.createElement('div');
  el.style.cssText = `position:absolute;visibility:hidden;width:${mm}mm`;
  document.body.appendChild(el);
  const px = el.getBoundingClientRect().width;
  document.body.removeChild(el);
  return px;
};

/**
 * Maps each top-level HTML block index to its A4 page index (0-based).
 * Uses the same block-level rules as the iframe pagination script.
 */
export const paginateContentToPageIndices = html => {
  if (typeof document === 'undefined') {
    return [];
  }

  const host = document.createElement('div');
  host.style.cssText = MEASURE_HOST_STYLE;
  host.innerHTML = html || '';
  document.body.appendChild(host);

  const nodes = Array.from(host.childNodes).filter(isMeaningfulNode);

  if (!nodes.length) {
    document.body.removeChild(host);
    return [];
  }

  const measurePage = document.createElement('div');
  measurePage.style.cssText = `height:${A4_PAGE_CONTENT_HEIGHT_MM}mm;overflow:hidden;width:100%;box-sizing:border-box`;
  const section = document.createElement('div');
  measurePage.appendChild(section);
  host.appendChild(measurePage);

  const maxPageContentHeightPx = mmToPx(A4_PAGE_CONTENT_HEIGHT_MM);
  const blockPageIndices = [];
  let currentPage = 0;

  nodes.forEach((node, index) => {
    const clone = node.cloneNode(true);
    section.appendChild(clone);

    if (section.scrollHeight > maxPageContentHeightPx) {
      section.removeChild(clone);
      currentPage += 1;
      section.innerHTML = '';
      section.appendChild(clone);
    }

    blockPageIndices[index] = currentPage;
  });

  document.body.removeChild(host);
  return blockPageIndices;
};

export const getPageIndexForBlockIndex = (html, blockIndex) => {
  const indices = paginateContentToPageIndices(html);
  if (!indices.length) {
    return 0;
  }

  const safeIndex = Math.max(0, Math.min(blockIndex, indices.length - 1));
  return indices[safeIndex] ?? 0;
};

const resolveTopLevelBlockNode = (lineBlot, editorRoot) => {
  const blockNode = lineBlot?.domNode;

  if (!blockNode) {
    return null;
  }

  if (blockNode.tagName === 'LI') {
    return blockNode.closest('ul, ol') || blockNode;
  }

  if (editorRoot.contains(blockNode)) {
    const topLevelMatch = Array.from(editorRoot.children).find(
      child => child === blockNode || child.contains(blockNode)
    );
    return topLevelMatch || blockNode;
  }

  return blockNode.closest(
    'p, h1, h2, h3, h4, h5, h6, ul, ol, blockquote, pre'
  );
};

/** Index of the top-level Quill block (p, h1, ul, …) for the current cursor. */
export const getBlockIndexAtQuillSelection = quill => {
  if (!quill?.root) {
    return 0;
  }

  const selection = quill.getSelection();
  const index = selection
    ? selection.index
    : Math.max(0, quill.getLength() - 1);
  const line = quill.getLine(index);

  if (!line?.[0]) {
    return 0;
  }

  const blockNode = resolveTopLevelBlockNode(line[0], quill.root);
  if (!blockNode) {
    return 0;
  }

  const topLevel = Array.from(quill.root.children);
  const blockIndex = topLevel.indexOf(blockNode);

  return blockIndex >= 0 ? blockIndex : 0;
};

export const getActivePageIndexFromQuill = (html, quill) => {
  const blockIndex = getBlockIndexAtQuillSelection(quill);
  return getPageIndexForBlockIndex(html, blockIndex);
};
